/**
 * Responsibility: how the Data Hub's figures are written, in one place.
 *
 * Three of these encode a decision the handoff note is explicit about, so
 * they are functions rather than inline expressions that could each drift:
 *
 * **An unknown total is a dash, never a zero.** Nobody publishes how many
 * graded cards eBay has ever listed, so `available` is null and `share` is
 * 0. Printing "0%" would read as "we have none of it", which is the
 * opposite of "we cannot know".
 *
 * **A count without its denominator says nothing.** 26 of 7,127 is a fact;
 * 26 is a number. So held is always written against available where there
 * is one.
 *
 * **Last updated carries the time as well as the date.** These libraries
 * gain examples through the day, and "today" cannot tell a harvest that ran
 * an hour ago from one that ran at midnight.
 */

/** Bytes as something a person reads, not a number with ten digits. */
export function readableBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** How much of what exists we hold, or a dash where there is no total. */
export function heldOf(held: number, available: number | null): string {
  if (available === null) return `${held.toLocaleString()} / —`;
  return `${held.toLocaleString()} / ${available.toLocaleString()}`;
}

/** The share as a percentage, or a dash. Never "0%" for "unknown". */
export function shareLabel(available: number | null, share: number): string {
  if (available === null) return '—';
  if (share > 0 && share < 0.001) return '<0.1%';
  return `${(share * 100).toFixed(1)}%`;
}

/** A date and a time, because these move through the day. */
export function stamp(iso: string | null): string {
  if (!iso) return 'never';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'never';
  return `${when.toLocaleDateString()} ${when.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * What a category label can be trusted to mean.
 *
 * `from_title` was read out of a seller's title and can be wrong. That is
 * the one worth flagging, so it is the one that gets a colour.
 */
export const CONFIDENCE_LABEL: Record<string, string> = {
  published: 'from the grader',
  from_title: 'read from the title',
  unknown: 'unknown',
};

/** Only the one that can be wrong is coloured. Absent is not the same as suspect. */
export const CONFIDENCE_COLOUR: Record<string, 'success' | 'warning' | 'default'> = {
  published: 'success',
  from_title: 'warning',
  unknown: 'default',
};
